import { LIMITS } from '@athena/shared'
import { Button, FieldMessage, linkClasses, TextInput, useLanguage, useTranslate } from '@athena/ui'
import { normalizeEmail } from '@athena/validation'
import { useEffect, useRef, useState, type FormEvent } from 'react'

import { fetchEmailSignInMode } from './providers.js'

import { AUTH_ENDPOINTS } from '../../lib/apollo.js'

const { SIGN_IN_CODE_LENGTH } = LIMITS

type State =
  | { step: 'email'; status: 'idle' | 'sending' | 'failed' | 'missing' | 'malformed' }
  | { step: 'code'; status: 'idle' | 'verifying' | 'invalid' | 'missing' | 'failed' }

/** Enough to catch a typo before a round trip; the server is what decides. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface EmailCodeFormProps {
  /** Defaults to the plain "email me a code". The invitation flow says more. */
  submitLabel?: string
  /**
   * Called once the code has been exchanged for a session.
   *
   * There is deliberately no `callbackPath` here, unlike the provider buttons
   * beside it. A code never leaves the tab: the session cookie arrives on the
   * response to this very POST, so where to go next is the caller's decision
   * to make in the app it is already running in, not a URL to hand to somebody
   * else and hope to get back.
   */
  onSignedIn: () => void
}

/**
 * Email sign-in: emailed codes in production, email-only in development.
 *
 * Not a mutation. Better Auth owns /api/auth/* and brings its own CSRF
 * handling, rate limiting and cookie logic; GraphQL exposes `me` and nothing
 * else about the mechanism (V1.md section 5).
 *
 * ── Why two steps in one component ──────────────────────────────────────
 *
 * They are one interaction. The person types an address, reads six digits off
 * their phone, and types them into the box that replaced the first one — in
 * the same tab, which is the reason this is a code and not a link. Splitting
 * it across two routes would put the address in a URL and lose the tab, which
 * is what the link flow did wrong.
 *
 * ── The confirmation says nothing ───────────────────────────────────────
 *
 * The heading after step one is identical whether or not the address has an
 * account, because the API's response is too. Any difference here would put
 * the account-enumeration oracle back after the server went to the trouble of
 * removing it.
 */
export function EmailCodeForm({ submitLabel, onSignedIn }: EmailCodeFormProps) {
  const t = useTranslate()
  const language = useLanguage()
  const [mode, setMode] = useState<'email-only' | 'otp' | null>(null)
  useEffect(() => {
    let active = true
    void fetchEmailSignInMode().then((mode) => {
      if (active) setMode(mode)
    })
    return () => {
      active = false
    }
  }, [])
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [state, setState] = useState<State>({ step: 'email', status: 'idle' })

  const busy = state.status === 'sending' || state.status === 'verifying'
  const emailRef = useRef<HTMLInputElement>(null)
  // A refused address takes focus back to its field, where the reason is.
  const refused =
    state.step === 'email' && (state.status === 'missing' || state.status === 'malformed')
  useEffect(() => {
    if (refused) emailRef.current?.focus()
  }, [refused, state])

  async function requestCode(event?: FormEvent) {
    event?.preventDefault()
    // One request at a time: Enter in the field submits too, and the button's
    // loading state alone does not stop that.
    if (busy || mode === null) return
    const address = normalizeEmail(email)
    if (address === '') return setState({ step: 'email', status: 'missing' })
    if (!EMAIL_SHAPE.test(address)) return setState({ step: 'email', status: 'malformed' })
    setState({ step: 'email', status: 'sending' })

    try {
      const response = await fetch(
        mode === 'email-only' ? AUTH_ENDPOINTS.signInWithEmail : AUTH_ENDPOINTS.sendCode,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'content-type': 'application/json',
            // Section 18: for an address with no account yet there is no stored
            // preference, so the language of the page that asked is the best the
            // API has to write the email in.
            'accept-language': language,
          },
          body: JSON.stringify(
            mode === 'email-only' ? { email: address } : { email: address, type: 'sign-in' },
          ),
        },
      )
      if (response.ok && mode === 'email-only') {
        onSignedIn()
        return
      }
      setState(response.ok ? { step: 'code', status: 'idle' } : { step: 'email', status: 'failed' })
    } catch {
      setState({ step: 'email', status: 'failed' })
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault()
    if (busy) return

    const typed = code.replace(/\s/g, '')
    if (typed.length !== SIGN_IN_CODE_LENGTH) {
      // Not "that code is wrong": nothing has been checked yet.
      setState({ step: 'code', status: 'missing' })
      return
    }

    setState({ step: 'code', status: 'verifying' })

    try {
      const response = await fetch(AUTH_ENDPOINTS.signInWithCode, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', 'accept-language': language },
        body: JSON.stringify({ email: normalizeEmail(email), otp: typed }),
      })

      if (!response.ok) {
        // Every refusal reads the same: wrong digits, an expired code, one
        // already spent, one burned by three wrong guesses. The API does not
        // distinguish them to the client and neither does this, because the
        // difference between "wrong" and "expired" is a hint worth having if
        // you are guessing.
        setState({ step: 'code', status: 'invalid' })
        setCode('')
        return
      }

      onSignedIn()
    } catch {
      setState({ step: 'code', status: 'failed' })
    }
  }

  if (state.step === 'code') {
    return (
      // `noValidate`: a missing code is said in Athena's words, under the
      // field, not in a browser tooltip beside them.
      <form noValidate onSubmit={submitCode} className="flex w-full flex-col gap-4">
        {/* The same surface the address was typed into, with the next step in
            it — not a new, louder panel. The heading is the step's name. */}
        <div role="status" className="flex flex-col gap-1">
          <h2 className="text-h4 font-semibold text-text">{t('auth.codeSentTitle')}</h2>
          <p className="break-words text-body-sm text-text-secondary">
            {t('auth.codeSentBody', [email.trim()])}
          </p>
        </div>

        <div className="w-full">
          <TextInput
            label={t('auth.codeLabel')}
            helperText={t('auth.codeHelper')}
            // `one-time-code` is what makes iOS and Android offer the digits
            // from the notification, which is the entire ergonomic argument
            // for a code over a link.
            autoComplete="one-time-code"
            inputMode="numeric"
            // A pattern rather than type="number", which brings a spinner, drops
            // leading zeros, and lets a person type `1e5`.
            pattern="[0-9]*"
            maxLength={SIGN_IN_CODE_LENGTH}
            autoFocus
            required
            value={code}
            onChange={(event) => setCode(event.target.value)}
            {...(state.status === 'invalid'
              ? { error: t('auth.codeInvalid') }
              : state.status === 'missing'
                ? { error: t('auth.codeRequired') }
                : {})}
          />
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full"
          loading={state.status === 'verifying'}
          loadingText={t('auth.verifying')}
        >
          {t('auth.codeSubmit')}
        </Button>

        {state.status === 'failed' ? (
          <FieldMessage tone="error">{t('auth.requestFailed')}</FieldMessage>
        ) : null}

        <div className="flex flex-wrap gap-4">
          <button
            type="button"
            className={linkClasses({ size: 'sm', layout: 'standalone' })}
            onClick={() => void requestCode()}
          >
            {t('auth.resend')}
          </button>
          <button
            type="button"
            className={linkClasses({ size: 'sm', layout: 'standalone' })}
            onClick={() => {
              setCode('')
              setState({ step: 'email', status: 'idle' })
            }}
          >
            {t('auth.useAnotherEmail')}
          </button>
        </div>
      </form>
    )
  }

  return (
    <form noValidate onSubmit={requestCode} className="flex w-full flex-col gap-4">
      <div className="w-full">
        <TextInput
          ref={emailRef}
          label={t('auth.emailLabel')}
          helperText={t(mode === 'email-only' ? 'auth.emailOnlyHelper' : 'auth.emailHelper')}
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
            if (state.status === 'missing' || state.status === 'malformed')
              setState({ step: 'email', status: 'idle' })
          }}
          {...(state.status === 'missing'
            ? { error: t('auth.emailRequired') }
            : state.status === 'malformed'
              ? { error: t('auth.emailInvalid') }
              : {})}
        />
      </div>

      <Button
        type="submit"
        size="lg"
        className="w-full"
        loading={state.status === 'sending'}
        disabled={mode === null}
        loadingText={t(mode === 'email-only' ? 'auth.verifying' : 'auth.sending')}
      >
        {mode === 'email-only' ? t('auth.submit') : (submitLabel ?? t('auth.submit'))}
      </Button>

      {state.status === 'failed' ? (
        <FieldMessage tone="error">{t('auth.requestFailed')}</FieldMessage>
      ) : null}
    </form>
  )
}
